import UpIcon from "@mui/icons-material/ExpandLessRounded";
import DownIcon from "@mui/icons-material/ExpandMoreRounded";
import { Button, Menu } from "@mui/material";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { formatDate } from "../../utils/formatDate";
import InputDatePickerComponent from "../input-date-picker/InputDatePickerComponent";
import "./InputDateRangePickerComponent.css";

const InputDateRangePickerComponent = ({
  label,
  minDate,
  maxEndDate,
  valueDateEnd,
  valueDateStart,
  onSelect,
  disableWeekends = false,
  limitToFiveYears = false,
}) => {
  const [anchorMenu, setAnchorMenu] = useState(null);
  const [dateEnd, setDateEnd] = useState(valueDateEnd);
  const [dateEndMax, setDateEndMax] = useState(dayjs());
  const [dateEndMin, setDateEndMin] = useState(minDate);
  const [dateStart, setDateStart] = useState(valueDateStart);
  const [selectedDate, setSelectedDate] = useState(null);
  const [enableEditEndDate, setEnableEditEndDate] = useState();
  const [today] = useState(dayjs());
  const isOptionOpen = Boolean(anchorMenu);

  useEffect(() => {
    setDateStart(valueDateStart);
  }, [valueDateStart]);

  useEffect(() => {
    setDateEnd(valueDateEnd);
  }, [valueDateEnd]);

  const updateDateStart = (date) => {
    setDateStart(date);
    setDateEnd(null);

    if (maxEndDate) {
      const max = dayjs(date).add(maxEndDate, "days");
      const dateEndMax = max.isAfter(today) ? today : max;

      setDateEndMax(dateEndMax);
    }

    setEnableEditEndDate(true);
    setDateEndMin(date);
  };

  const updateDateEnd = (date) => {
    if (!date) return;

    setDateEnd(date);

    const label = dayjs(date).isSame(dateStart, "day")
      ? formatDate.toShortDate(dateStart)
      : `${formatDate.toShortDate(dateStart)} - ${formatDate.toShortDate(
          date,
        )}`;

    const selected = {
      label,
      type: "date",
      value: {
        dateEnd: formatDate.toAPIDate(date),
        dateStart: formatDate.toAPIDate(dateStart),
      },
    };

    onSelect(selected);
    setSelectedDate(selected);

    closeOption(true);
  };

  const openOption = (event) => {
    setAnchorMenu(event.currentTarget);
  };

  const closeOption = useCallback(
    (forced) => {
      if (!forced && !dateEnd) {
        return;
      }

      setAnchorMenu(null);
    },
    [dateEnd],
  );

  return (
    <div>
      <Button
        color={isOptionOpen ? "primary" : "secondary"}
        endIcon={isOptionOpen ? <UpIcon /> : <DownIcon />}
        size='medium'
        variant='outlined'
        onClick={openOption}
      >
        <p className='text-sm'>
          {valueDateStart && valueDateEnd
            ? `${formatDate.toShortDate(
                valueDateStart,
              )} - ${formatDate.toShortDate(valueDateEnd)}`
            : selectedDate
            ? selectedDate?.label
            : label}
        </p>
      </Button>

      <Menu
        anchorEl={anchorMenu}
        className='input-date-range-picker-menu'
        open={isOptionOpen}
        onClose={() => closeOption()}
      >
        <div className='input-date-range-picker-container'>
          <div className='input-date-range-picker-group'>
            <p className='input-date-range-picker-label'>Start Date</p>

            <InputDatePickerComponent
              minDate={minDate}
              maxDate={today}
              value={dateStart}
              onChange={updateDateStart}
              disableWeekends={disableWeekends}
              limitToFiveYears={limitToFiveYears}
            />
          </div>

          <p className='input-date-range-picker-separator'>&ndash;</p>

          <div className='input-date-range-picker-group'>
            <p className='input-date-range-picker-label'>End Date</p>

            <InputDatePickerComponent
              disabled={!dateStart || !enableEditEndDate}
              maxDate={dateEndMax}
              minDate={dateEndMin}
              value={dateEnd}
              onChange={updateDateEnd}
              disableWeekends={disableWeekends}
              defaultCalendarMonth={dateStart}
            />
          </div>
        </div>
      </Menu>
    </div>
  );
};

export default InputDateRangePickerComponent;
